import { Amplify, Auth } from 'aws-amplify';
import { TokenTypes } from './apiAsyncThunkCreator';
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import { IUserLoginRequest } from '../slices/auth/login.thunks';
import awsConfig from '../aws-config';
import qs from 'qs';

const AZURE_CUSTOM_PROVIDER = 'Microsoft';

export enum CognitoAction {
  /** @deprecated */
  SignUp = 'SIGNUP', // This was used for trial accounts
  Login = 'LOGIN',
  SignUpPro = 'SIGNUP_PRO',
  SignUpLite = 'SIGNUP_LITE'
}

export enum CognitoEvents {
  SignInFailure = 'signIn_failure',
  CustomOAuthState = 'customOAuthState',
  CognitoHostedUi = 'cognitoHostedUI',
  SignInSuccess = 'signIn'
}

export enum CognitoErrors {
  UserIsNotEnabled = 'User+is+not+enabled',
  InvalidGrant = 'invalid_grant',
  IdentityProviderMismatch = 'Error+in+SAML+response+processing%3A+PreSignUp+failed+with+error+identity+provider+mismatch.+',
}

export const loadAmplifyConfig = (userPoolWebClientId?: string | null): void => {
  const newAwsConfig = (() => {
    if(userPoolWebClientId) {
      return {
        ...awsConfig,
        Auth: {
          ...awsConfig.Auth,
          userPoolWebClientId
        }
      };
    }
    return { ...awsConfig };
  })();
  Amplify.configure(newAwsConfig);
};

export const refreshCognitoIdToken = async (): Promise<string> => {
  try {
    const [session, currentUser] = await Promise.all([Auth.currentSession(), Auth.currentAuthenticatedUser()]);
    await currentUser.refreshSession(session.getRefreshToken(), (err: unknown) => {
      if (err) {
        throw err;
      }
    });
    const newSession = await Auth.currentSession();
    return `${TokenTypes.Cognito} ${newSession.getIdToken().getJwtToken()}`;
  } catch (error) {
    return '';
  }
};

export const cognitoLogout = async (): Promise<void> => {
  Auth.signOut();
};

export const cognitoCurrentToken = async (): Promise<string> => {
  let sessionOrNull;
  try {
    sessionOrNull = await Auth.currentSession();
  } catch (err) {
    return '';
  }
  return sessionOrNull.getIdToken().getJwtToken();
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const cognitoCurrentUser = async(): Promise<any> => {
  try {
    return Auth.currentUserInfo();
  } catch (err) {
    return null;
  }
};

const buildAndGetCustomState = (action: CognitoAction) => {
  const queryParamsObj = qs.parse( window.location.search.replace('?', ''));
  const { ...queryParams } = queryParamsObj;
  return JSON.stringify({ action, ...queryParams });
};

export const signInWithGoogle = (action: CognitoAction = CognitoAction.Login): void => {
  const customState = buildAndGetCustomState(action);
  Auth.federatedSignIn({ provider: CognitoHostedUIIdentityProvider.Google, customState });
};

export const signInWithAzure = (action: CognitoAction = CognitoAction.Login): void => {
  const customState = buildAndGetCustomState(action);
  Auth.federatedSignIn({ customProvider: AZURE_CUSTOM_PROVIDER, customState });
};

export const signInWithAmazon = (params: IUserLoginRequest): void => {
  Auth.signIn(params.email, params.password);
};

export const signInWithCustomProvider = (providerName: string, action: CognitoAction = CognitoAction.Login): void => {
  const customState = buildAndGetCustomState(action);
  Auth.federatedSignIn({ customProvider: providerName, customState });
};
